import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

const KNOWN_PARAMS = [ "search", "page", "pageSize", "sortBy", "sortOrder" ];

// Search once typing pauses, not per keystroke — holding a key down otherwise
// fires a request per character and trips the API rate limit (429).
const SEARCH_DEBOUNCE_MS = 300;

// Longer terms can't match anything useful; the cap also keeps a pasted wall
// of text (or one arriving via ?search=) out of the request URL.
export const SEARCH_MAX_LENGTH = 100;

interface UseSearchFiltersUrlSyncOptions {
    initialFilters?: Record<string, unknown>;
    enableSearch?: boolean;
    /**
     * URL params owned by the host page (e.g. the `tab` of a tabbed page).
     * They are neither read as filters nor removed when the filters change.
     */
    preservedParams?: readonly string[];
}

export function useSearchFiltersUrlSync<TQuery>({
    initialFilters = {} as Record<string, unknown>,
    enableSearch = false,
    preservedParams = [],
}: UseSearchFiltersUrlSyncOptions = {}) {
    const [ searchParams, setSearchParams ] = useSearchParams();

    // Keyed by content so an inline array literal doesn't re-run the effects.
    const preservedKey = preservedParams.join(",");
    const isFilterParam = useCallback(
        (key: string) =>
            !KNOWN_PARAMS.includes(key) && !preservedKey.split(",").includes(key),
        [ preservedKey ]
    );

    // Initialize search from URL
    const [ searchValue, setSearchValue ] = useState(
        (searchParams.get("search") || "").slice(0, SEARCH_MAX_LENGTH)
    );
    const debouncedSearch = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);

    // Initialize filters from URL
    const initializeFiltersFromURL = useCallback((): TQuery => {
        const urlFilters: Record<string, unknown> = {};
        // Extract filter params from URL (exclude pagination/search and page-owned params)
        searchParams.forEach((value, key) => {
            if (isFilterParam(key)) {
                // Try to parse as boolean or number, otherwise keep as string
                if (value === "true") urlFilters[ key ] = true;
                else if (value === "false") urlFilters[ key ] = false;
                else if (!isNaN(Number(value)) && value !== "")
                    urlFilters[ key ] = Number(value);
                else if (value.includes(",")) urlFilters[ key ] = value.split(",");
                else urlFilters[ key ] = value;
            }
        });
        return { ...initialFilters, ...urlFilters } as TQuery;
    }, [ searchParams, initialFilters, isFilterParam ]);

    const [ filters, setFilters ] = useState<TQuery>(initializeFiltersFromURL);

    // Update URL when search changes
    useEffect(() => {
        if (!enableSearch) return;

        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                if (debouncedSearch) {
                    params.set("search", debouncedSearch);
                } else {
                    params.delete("search");
                }
                // Reset to page 1 when search changes
                params.set("page", "1");
                return params;
            },
            { replace: true }
        );
    }, [ debouncedSearch, enableSearch, setSearchParams ]);

    // Update URL when filters change
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                // Remove all filter params first
                prev.forEach((_, key) => {
                    if (isFilterParam(key)) {
                        params.delete(key);
                    }
                });
                // Add current filter params
                const filtersObj = filters as Record<string, unknown>;
                Object.entries(filtersObj).forEach(([ key, value ]) => {
                    if (value !== undefined && value !== null && value !== "") {
                        if (Array.isArray(value)) {
                            params.set(key, value.join(","));
                        } else {
                            params.set(key, String(value));
                        }
                    }
                });
                // Reset to page 1 when filters change
                params.set("page", "1");
                return params;
            },
            { replace: true }
        );
    }, [ filters, setSearchParams, isFilterParam ]);

    // Merge search and filters into base query
    const baseQuery = useMemo(
        () =>
        ({
            ...(enableSearch && debouncedSearch ? { search: debouncedSearch } : {}),
            ...filters,
        } as TQuery),
        [ enableSearch, debouncedSearch, filters ]
    );

    return {
        searchValue,
        setSearchValue,
        filters,
        setFilters,
        baseQuery,
    };
}
